import { registerWeapon, lookupWeapon, createProjectile } from './registry';
import { SMALL_CRATER, HUGE_CRATER } from '../terrain/crater-shapes';

const BLACK_HOLE = registerWeapon({
  id: 'black_hole',
  name: 'Black Hole',
  description: 'Spawns a vortex that pulls everything in, then detonates violently.',
  tier: 'EPIC',
  rarity: 'epic',
  projectileRadius: 8,
  projectileSpeed: 0.50,
  color: 0x8800ff,
  gravity: 0.15,
  bounces: 0,
  bounceEnergyRetention: 0.85,
  damage: 8,
  blastRadius: 35,
  knockbackMult: 0.02,
  knockbackRadius: 0.8,
  shakeIntensity: 4.0,
  particleScale: 0.4,
  craterShape: SMALL_CRATER,
  canDestroyWireframe: true,
  wireframeDamageScale: 0.8,
  onImpact(proj, x, y, _hitType, _hitIndex, state, fx) {
    // Spawn visible vortex
    const wellWeapon = lookupWeapon('black_hole_well');
    const well = createProjectile(
      wellWeapon, x, y, 0, 0,
      proj.owner, state.frameCount, 1,
    );
    well.ignoreTerrain = true;
    well.ignoreTanks = true;
    well.ignoreWind = true;
    well.behaviorState.lifetime = 0;
    // Vortex spawn effects
    fx.push({ type: 'screen_shake', intensity: 8, decay: 0.9 });
    fx.push({ type: 'particles', method: 'spawnRichExplosion', args: [x, y, 0x8800ff, 40, 0.6] });
    return [{
      spawnFrame: state.frameCount + 2,
      spawned: false,
      projectile: well,
    }];
  },
});

// Black hole well sub-weapon (not in lottery pool)
registerWeapon({
  id: 'black_hole_well',
  name: 'Gravity Well',
  description: '',
  tier: 'SUB',
  rarity: 'common',
  projectileRadius: 14,
  projectileSpeed: 0,
  color: 0x8800ff,
  gravity: 0,
  bounces: 0,
  bounceEnergyRetention: 0,
  damage: 55,
  blastRadius: 160,
  knockbackMult: 0.14,
  knockbackRadius: 1.5,
  shakeIntensity: 1.5,
  particleScale: 3.0,
  craterShape: HUGE_CRATER,
  canDestroyWireframe: true,
  wireframeDamageScale: 0.5,
  onTick(proj, state, fx) {
    proj.behaviorState.lifetime = (proj.behaviorState.lifetime || 0) + 1;
    const t = proj.behaviorState.lifetime;

    // Pull phase: 90 frames of gravitational pull
    if (t < 90) {
      const pullRadius = 300;
      // Pull force ramps up over time (gentle start → violent end)
      const ramp = 0.3 + (t / 90) * 0.7;
      const pullForce = 0.5 * ramp;
      for (const p of state.players) {
        if (!p.alive) continue;
        const dx = proj.x - p.x;
        const dy = proj.y - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < pullRadius && dist > 10) {
          const strength = pullForce * (1 - dist / pullRadius);
          p.vx += (dx / dist) * strength;
          p.vy += (dy / dist) * strength;
          p.settled = false;
        }
      }

      // Vortex particles — swirl inward every few frames
      if (t % 3 === 0) {
        const angle = t * 0.4;
        const dist = 80 + Math.sin(t * 0.1) * 30;
        const px = proj.x + Math.cos(angle) * dist;
        const py = proj.y + Math.sin(angle) * dist;
        fx.push({ type: 'particles', method: 'spawnRichExplosion', args: [px, py, 0x8800ff, 15, 0.15] });
      }
      // Secondary swirl (opposite side)
      if (t % 5 === 0) {
        const angle2 = t * -0.3 + Math.PI;
        const dist2 = 60 + Math.cos(t * 0.15) * 25;
        const px2 = proj.x + Math.cos(angle2) * dist2;
        const py2 = proj.y + Math.sin(angle2) * dist2;
        fx.push({ type: 'particles', method: 'spawnRichExplosion', args: [px2, py2, 0xaa44ff, 12, 0.1] });
      }

      // Growing screen shake — builds tension
      if (t % 10 === 0) {
        const shakeScale = 1 + (t / 90) * 4;
        fx.push({ type: 'screen_shake', intensity: shakeScale, decay: 0.85 });
      }

      return null;
    }

    // Detonation — violent outward explosion
    fx.push({ type: 'screen_shake', intensity: 25, decay: 0.88 });
    fx.push({ type: 'screen_flash', color: 0xffffff, alpha: 0.4 });
    return 'detonate';
  },
});
